import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "../config/supabase";
import { normalizeTenantSlug } from "../utils/tenantSlug";

interface ResolvedTenant {
  clientId: string;
  clientName: string;
  clientSlug: string;
}

interface TenantSlugContextValue {
  tenantSlug: string | null;
  resolvedTenant: ResolvedTenant | null;
  resolvedClientId: string | null;
  isResolvingTenant: boolean;
  tenantNotFound: boolean;
}

const defaultValue: TenantSlugContextValue = {
  tenantSlug: null,
  resolvedTenant: null,
  resolvedClientId: null,
  isResolvingTenant: false,
  tenantNotFound: false,
};

const TenantSlugContext = createContext<TenantSlugContextValue>(defaultValue);

interface TenantSlugProviderProps {
  tenantSlug: string;
  children: ReactNode;
}

export const TenantSlugProvider = ({ tenantSlug, children }: TenantSlugProviderProps) => {
  const normalizedSlug = useMemo(() => normalizeTenantSlug(tenantSlug), [tenantSlug]);
  const [resolvedTenant, setResolvedTenant] = useState<ResolvedTenant | null>(null);
  const [isResolvingTenant, setIsResolvingTenant] = useState(true);
  const [tenantNotFound, setTenantNotFound] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const resolveTenant = async () => {
      if (!normalizedSlug) {
        if (!isCancelled) {
          setResolvedTenant(null);
          setTenantNotFound(true);
          setIsResolvingTenant(false);
        }
        return;
      }

      setIsResolvingTenant(true);
      setTenantNotFound(false);

      const { data, error } = await supabase.rpc("get_client_by_slug", {
        p_slug: normalizedSlug,
      });

      if (isCancelled) return;

      if (error) {
        console.error("Erro ao resolver tenant por slug:", error);
        setResolvedTenant(null);
        setTenantNotFound(true);
        setIsResolvingTenant(false);
        return;
      }

      const row = Array.isArray(data) ? data[0] : data;

      if (!row?.clientId) {
        setResolvedTenant(null);
        setTenantNotFound(true);
        setIsResolvingTenant(false);
        return;
      }

      setResolvedTenant({
        clientId: row.clientId,
        clientName: row.clientName,
        clientSlug: row.clientSlug,
      });
      setTenantNotFound(false);
      setIsResolvingTenant(false);
    };

    void resolveTenant();

    return () => {
      isCancelled = true;
    };
  }, [normalizedSlug]);

  const value = useMemo<TenantSlugContextValue>(
    () => ({
      tenantSlug: normalizedSlug || null,
      resolvedTenant,
      resolvedClientId: resolvedTenant?.clientId ?? null,
      isResolvingTenant,
      tenantNotFound,
    }),
    [isResolvingTenant, normalizedSlug, resolvedTenant, tenantNotFound]
  );

  return <TenantSlugContext.Provider value={value}>{children}</TenantSlugContext.Provider>;
};

export const useTenantSlug = () => useContext(TenantSlugContext);
